import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Upload, X, Loader2, Package, Award, Inbox, Mail, Phone, CalendarClock } from "lucide-react";
import { API, imageUrl } from "../config";

const CATEGORIES = ["Yapay Kaplama", "Kaplama", "Linea", "Vinterio", "Kereste", "Boyalı Kaplama", "Kontrplak"];
const SUB_BY_CAT = {
  "Kaplama": ["Kök Kaplama", "Piramit Kaplama", "Papel Kaplama"],
  "Yapay Kaplama": ["Normal", "3D Yüzey"],
  "Kereste": ["Afrika", "Amerika", "Avrupa", "Uzak Doğu"],
};
const EMPTY = { name: "", category: "Yapay Kaplama", subcategory: "", image: "", description: "" };

const formatMessageDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || "-";
  return date.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function Yonetim() {
  const [token, setToken] = useState(() => localStorage.getItem("io_admin_token"));
  const [authed, setAuthed] = useState(null);
  const [pw, setPw] = useState("");
  const [authErr, setAuthErr] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState("");

  const [tab, setTab] = useState("products");
  const [certs, setCerts] = useState([]);
  const [certForm, setCertForm] = useState({ title: "", image: "" });
  const [certUploading, setCertUploading] = useState(false);
  const [certSaving, setCertSaving] = useState(false);
  const certFileRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (e) {
      toast.error("Ürünler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const loadCerts = async () => {
    try {
      const res = await axios.get(`${API}/certificates`);
      setCerts(res.data);
    } catch (e) {
      /* ignore */
    }
  };

  const loadMessages = async () => {
    if (!token) return;
    setMessagesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/messages`, auth());
      setMessages(res.data);
    } catch (e) {
      toast.error("Mesajlar yüklenemedi");
    } finally {
      setMessagesLoading(false);
    }
  };

  useEffect(() => { load(); loadCerts(); }, []);

  useEffect(() => {
    if (authed && token) loadMessages();
    // eslint-disable-next-line
  }, [authed, token]);

  useEffect(() => {
    if (!token) { setAuthed(false); return; }
    axios.get(`${API}/admin/verify`, auth())
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem("io_admin_token"); setToken(null); setAuthed(false); });
    // eslint-disable-next-line
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    setAuthErr("");
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem("io_admin_token", res.data.token);
      setToken(res.data.token);
      setAuthed(true);
      setPw("");
    } catch (err) {
      setAuthErr("Hatalı şifre. Tekrar deneyin.");
    } finally {
      setAuthLoading(false);
    }
  };

  const doLogout = () => {
    localStorage.removeItem("io_admin_token");
    setToken(null);
    setAuthed(false);
  };

  const handleCertUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCertUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` } });
      setCertForm((f) => ({ ...f, image: res.data.url }));
      toast.success("Görsel yüklendi");
    } catch (e) {
      toast.error("Görsel yüklenemedi (yalnızca jpg, png, gif, webp)");
    } finally {
      setCertUploading(false);
    }
  };

  const saveCert = async (e) => {
    e.preventDefault();
    if (!certForm.image) { toast.error("Lütfen bir sertifika görseli ekleyin"); return; }
    setCertSaving(true);
    try {
      await axios.post(`${API}/certificates`, certForm, auth());
      toast.success("Sertifika eklendi");
      setCertForm({ title: "", image: "" });
      loadCerts();
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally {
      setCertSaving(false);
    }
  };

  const removeCert = async (c) => {
    if (!window.confirm("Bu sertifikayı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/certificates/${c.id}`, auth());
      toast.success("Sertifika silindi");
      setCerts((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, category: p.category, subcategory: p.subcategory || "", image: p.image, description: p.description }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` } });
      setForm((f) => ({ ...f, image: res.data.url }));
      toast.success("Görsel yüklendi");
    } catch (e) {
      toast.error("Görsel yüklenemedi (yalnızca jpg, png, gif, webp)");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) { toast.error("Ad ve kategori zorunludur"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/products/${editing.id}`, form, auth());
        toast.success("Ürün güncellendi");
      } else {
        await axios.post(`${API}/products`, form, auth());
        toast.success("Ürün eklendi");
      }
      closeForm();
      load();
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`"${p.name}" ürününü silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/products/${p.id}`, auth());
      toast.success("Ürün silindi");
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const removeMessage = async (m) => {
    if (!window.confirm(`"${m.name}" kişisinden gelen mesajı silmek istediğinize emin misiniz?`)) return;
    setDeletingMessageId(m.id);
    try {
      await axios.delete(`${API}/admin/messages/${m.id}`, auth());
      toast.success("Mesaj silindi");
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
    } catch (e) {
      toast.error("Mesaj silinemedi");
    } finally {
      setDeletingMessageId("");
    }
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0C0B]">
        <div className="font-sans uppercase tracking-[0.3em] text-xs text-[#8C8681] animate-pulse">Yükleniyor...</div>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0D0C0B] px-6">
        <motion.form
          data-testid="admin-login-form"
          onSubmit={doLogin}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm text-center"
        >
          <p className="font-sans uppercase tracking-[0.3em] text-xs text-[#7BA05B] mb-6">Yönetim</p>
          <h1 className="font-serif text-4xl text-[#F2EFEB] mb-4">Şifre Gerekli</h1>
          <p className="text-[#8C8681] mb-8 text-sm">Bu paneli yalnızca yetkili kişi düzenleyebilir.</p>
          <input
            data-testid="admin-password-input"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Yönetici şifresi"
            className="w-full bg-[#1A1715] border border-white/15 rounded-lg px-4 py-4 text-[#F2EFEB] text-center outline-none focus:border-[#7BA05B] transition-colors mb-4"
          />
          {authErr && <p data-testid="admin-login-error" className="text-red-400 text-sm mb-4">{authErr}</p>}
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={authLoading}
            className="w-full flex items-center justify-center gap-2 bg-[#F2EFEB] text-[#0D0C0B] hover:bg-[#7BA05B] hover:text-[#0D0C0B] py-4 rounded-full font-sans uppercase tracking-[0.2em] text-xs transition-all duration-300 disabled:opacity-50"
          >
            {authLoading && <Loader2 size={14} className="animate-spin" />}
            Giriş Yap
          </button>
          <Link to="/" className="mt-6 inline-block text-[#8C8681] hover:text-[#F2EFEB] transition-colors font-sans uppercase tracking-[0.2em] text-[10px]">← Ana sayfa</Link>
        </motion.form>
      </div>
    );
  }

  return (
    <div data-testid="yonetim-page" className="min-h-screen bg-[#0D0C0B]">
      <header className="border-b border-white/10 backdrop-blur-xl bg-[#0D0C0B]/70 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="back-home-link" className="text-[#8C8681] hover:text-[#F2EFEB] transition-colors">
              <ArrowLeft strokeWidth={1.5} size={18} />
            </Link>
            <span className="font-serif text-xl text-[#F2EFEB]">Yönetim</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              data-testid="tab-products"
              onClick={() => setTab("products")}
              className={`font-sans uppercase tracking-[0.15em] text-xs px-4 py-2.5 rounded-full border transition-all duration-300 ${
                tab === "products" ? "bg-[#F2EFEB] text-[#0D0C0B] border-[#F2EFEB]" : "text-[#8C8681] border-white/15 hover:text-[#F2EFEB]"
              }`}
            >
              Ürünler
            </button>
            <button
              data-testid="tab-messages"
              onClick={() => setTab("messages")}
              className={`font-sans uppercase tracking-[0.15em] text-xs px-4 py-2.5 rounded-full border transition-all duration-300 ${
                tab === "messages" ? "bg-[#F2EFEB] text-[#0D0C0B] border-[#F2EFEB]" : "text-[#8C8681] border-white/15 hover:text-[#F2EFEB]"
              }`}
            >
              Mesaj Kutusu
            </button>
            <button
              data-testid="tab-certificates"
              onClick={() => setTab("certificates")}
              className={`font-sans uppercase tracking-[0.15em] text-xs px-4 py-2.5 rounded-full border transition-all duration-300 ${
                tab === "certificates" ? "bg-[#F2EFEB] text-[#0D0C0B] border-[#F2EFEB]" : "text-[#8C8681] border-white/15 hover:text-[#F2EFEB]"
              }`}
            >
              Sertifikalar
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={doLogout}
              className="font-sans uppercase tracking-[0.15em] text-xs px-4 py-2.5 rounded-full border border-white/15 text-[#8C8681] hover:text-[#F2EFEB] hover:border-[#7BA05B] transition-all duration-300"
            >
              Çıkış
            </button>
          </div>
        </div>
      </header>

      {tab === "products" ? (
      <main className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <Package strokeWidth={1.5} className="text-[#C67D57]" />
            <h1 className="font-serif text-3xl md:text-4xl text-[#F2EFEB]">
              Ürünler <span className="text-[#8C8681] text-xl">({products.length})</span>
            </h1>
          </div>
          <button
            data-testid="add-product-btn"
            onClick={openNew}
            className="flex items-center gap-2 bg-[#F2EFEB] text-[#0D0C0B] hover:bg-[#C67D57] hover:text-[#F2EFEB] px-5 py-3 rounded-full font-sans uppercase tracking-[0.15em] text-xs transition-all duration-300"
          >
            <Plus size={15} strokeWidth={1.5} /> Yeni Ürün
          </button>
        </div>

        {loading ? (
          <div className="text-[#8C8681] font-sans uppercase tracking-[0.2em] text-xs">Yükleniyor...</div>
        ) : products.length === 0 ? (
          <div data-testid="no-products" className="text-[#8C8681] py-20 text-center border border-white/10 rounded-lg">
            Henüz ürün yok. "Yeni Ürün" ile ekleyin.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="group border border-white/10 rounded-lg overflow-hidden bg-[#1A1715]">
                <div className="aspect-[4/3] overflow-hidden bg-[#0D0C0B]">
                  {p.image ? (
                    <img src={imageUrl(p.image)} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#8C8681] text-xs">Görsel yok</div>
                  )}
                </div>
                <div className="p-5">
                  <span className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#C67D57]">{p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}</span>
                  <h3 className="font-serif text-xl text-[#F2EFEB] mt-1">{p.name}</h3>
                  {p.description && <p className="text-[#8C8681] text-sm mt-2 line-clamp-2">{p.description}</p>}
                  <div className="flex gap-2 mt-4">
                    <button
                      data-testid={`edit-product-${p.id}`}
                      onClick={() => openEdit(p)}
                      className="flex items-center gap-1.5 border border-white/15 text-[#F2EFEB] hover:border-[#C67D57] px-3 py-2 rounded-full text-xs transition-colors"
                    >
                      <Pencil size={12} strokeWidth={1.5} /> Düzenle
                    </button>
                    <button
                      data-testid={`delete-product-${p.id}`}
                      onClick={() => remove(p)}
                      className="flex items-center gap-1.5 border border-white/15 text-[#F2EFEB] hover:border-red-500 hover:text-red-400 px-3 py-2 rounded-full text-xs transition-colors"
                    >
                      <Trash2 size={12} strokeWidth={1.5} /> Sil
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      ) : tab === "messages" ? (
      <main data-testid="admin-messages-panel" className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        <div className="flex items-center gap-3 mb-10">
          <Inbox strokeWidth={1.5} className="text-[#C67D57]" />
          <h1 data-testid="admin-message-count" className="font-serif text-3xl md:text-4xl text-[#F2EFEB]">
            Mesaj Kutusu <span className="text-[#8C8681] text-xl">({messages.length})</span>
          </h1>
        </div>

        {messagesLoading ? (
          <div data-testid="messages-loading" className="text-[#8C8681] font-sans uppercase tracking-[0.2em] text-xs">Yükleniyor...</div>
        ) : messages.length === 0 ? (
          <div data-testid="no-messages" className="text-[#8C8681] py-16 text-center border border-white/10 rounded-lg">
            Henüz iletişim mesajı yok.
          </div>
        ) : (
          <div data-testid="admin-messages-list" className="space-y-5">
            {messages.map((m) => (
              <div key={m.id} data-testid={`admin-message-${m.id}`} className="border border-white/10 rounded-2xl bg-[#1A1715] p-6 md:p-7">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                  <div className="space-y-4 flex-1 min-w-0">
                    <div className="space-y-2">
                      <span data-testid={`admin-message-date-${m.id}`} className="inline-flex items-center gap-2 font-sans uppercase tracking-[0.18em] text-[10px] text-[#C67D57]">
                        <CalendarClock size={13} strokeWidth={1.5} />
                        {formatMessageDate(m.created_at)}
                      </span>
                      <h2 data-testid={`admin-message-name-${m.id}`} className="font-serif text-2xl text-[#F2EFEB] break-words">{m.name}</h2>
                      <p data-testid={`admin-message-subject-${m.id}`} className="text-sm text-[#8C8681] break-words">
                        {m.subject || "Konu belirtilmemiş"}
                      </p>
                    </div>

                    <div className="flex flex-col md:flex-row gap-3 md:gap-6 text-sm">
                      <a
                        data-testid={`admin-message-email-${m.id}`}
                        href={`mailto:${m.email}`}
                        className="inline-flex items-center gap-2 text-[#F2EFEB] hover:text-[#7BA05B] transition-colors break-all"
                      >
                        <Mail size={15} strokeWidth={1.5} className="text-[#8C8681]" />
                        {m.email}
                      </a>
                      <a
                        data-testid={`admin-message-phone-${m.id}`}
                        href={m.phone ? `tel:${m.phone.replace(/\s/g, "")}` : undefined}
                        className={`inline-flex items-center gap-2 break-all ${m.phone ? "text-[#F2EFEB] hover:text-[#C67D57] transition-colors" : "text-[#8C8681] cursor-default"}`}
                      >
                        <Phone size={15} strokeWidth={1.5} className="text-[#8C8681]" />
                        {m.phone || "Telefon belirtilmemiş"}
                      </a>
                    </div>

                    <div data-testid={`admin-message-body-${m.id}`} className="rounded-2xl border border-white/10 bg-[#12100F] px-4 py-4 text-[#C9C4BD] whitespace-pre-wrap leading-7 break-words">
                      {m.message}
                    </div>
                  </div>

                  <div className="md:pl-6 md:border-l md:border-white/10">
                    <button
                      data-testid={`delete-message-${m.id}`}
                      onClick={() => removeMessage(m)}
                      disabled={deletingMessageId === m.id}
                      className="inline-flex items-center justify-center gap-2 border border-white/15 text-[#F2EFEB] hover:border-red-500 hover:text-red-400 px-4 py-3 rounded-full text-xs font-sans uppercase tracking-[0.18em] transition-colors disabled:opacity-50"
                    >
                      {deletingMessageId === m.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={1.5} />}
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      ) : (
      <main className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        <div className="flex items-center gap-3 mb-10">
          <Award strokeWidth={1.5} className="text-[#C67D57]" />
          <h1 className="font-serif text-3xl md:text-4xl text-[#F2EFEB]">
            Sertifikalar <span className="text-[#8C8681] text-xl">({certs.length})</span>
          </h1>
        </div>

        <form data-testid="cert-form" onSubmit={saveCert} className="border border-white/10 rounded-lg p-6 bg-[#1A1715] mb-12">
          <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681] mb-4">Yeni Sertifika Ekle</p>
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-48 shrink-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden border border-white/10 bg-[#0D0C0B] flex items-center justify-center">
                {certForm.image ? (
                  <img src={imageUrl(certForm.image)} alt="önizleme" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[#8C8681] text-xs">Önizleme</span>
                )}
              </div>
            </div>
            <div className="flex-1 space-y-4">
              <input
                data-testid="cert-title-input"
                value={certForm.title}
                onChange={(e) => setCertForm({ ...certForm, title: e.target.value })}
                placeholder="Başlık (opsiyonel), örn: ISO 9001"
                className="w-full bg-[#0D0C0B] border border-white/15 rounded-lg px-4 py-3 text-[#F2EFEB] outline-none focus:border-[#C67D57] transition-colors"
              />
              <div className="flex gap-2">
                <input ref={certFileRef} type="file" accept="image/*" onChange={handleCertUpload} className="hidden" data-testid="cert-file-input" />
                <button
                  type="button"
                  data-testid="cert-upload-btn"
                  onClick={() => certFileRef.current?.click()}
                  disabled={certUploading}
                  className="flex items-center gap-2 border border-white/15 text-[#F2EFEB] hover:border-[#C67D57] px-4 py-3 rounded-lg text-sm transition-colors disabled:opacity-50"
                >
                  {certUploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} strokeWidth={1.5} />}
                  Dosya Yükle
                </button>
              </div>
              <input
                data-testid="cert-url-input"
                value={certForm.image}
                onChange={(e) => setCertForm({ ...certForm, image: e.target.value })}
                placeholder="veya görsel bağlantısı (URL) yapıştırın"
                className="w-full bg-[#0D0C0B] border border-white/15 rounded-lg px-4 py-2.5 text-[#F2EFEB] text-sm outline-none focus:border-[#C67D57] transition-colors"
              />
              <button
                data-testid="cert-save-btn"
                type="submit"
                disabled={certSaving}
                className="flex items-center gap-2 bg-[#F2EFEB] text-[#0D0C0B] hover:bg-[#C67D57] hover:text-[#F2EFEB] px-6 py-3 rounded-full font-sans uppercase tracking-[0.15em] text-xs transition-all duration-300 disabled:opacity-50"
              >
                {certSaving && <Loader2 size={14} className="animate-spin" />}
                <Plus size={15} strokeWidth={1.5} /> Ekle
              </button>
            </div>
          </div>
        </form>

        {certs.length === 0 ? (
          <div data-testid="no-certificates" className="text-[#8C8681] py-16 text-center border border-white/10 rounded-lg">
            Henüz sertifika yok. Yukarıdan ekleyin.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {certs.map((c) => (
              <div key={c.id} data-testid={`admin-cert-${c.id}`} className="group border border-white/10 rounded-lg overflow-hidden bg-[#1A1715]">
                <div className="aspect-[3/4] overflow-hidden bg-[#0D0C0B]">
                  <img src={imageUrl(c.image)} alt={c.title || "Sertifika"} className="w-full h-full object-cover" />
                </div>
                <div className="p-4 flex items-center justify-between gap-2">
                  <span className="font-serif text-sm text-[#F2EFEB] truncate">{c.title || "Sertifika"}</span>
                  <button
                    data-testid={`delete-cert-${c.id}`}
                    onClick={() => removeCert(c)}
                    className="shrink-0 text-[#8C8681] hover:text-red-400 transition-colors"
                    aria-label="Sil"
                  >
                    <Trash2 size={15} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      )}

      <AnimatePresence>
        {showForm && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={closeForm}
          >
            <motion.div
              data-testid="product-form"
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#1A1715] border border-white/10 rounded-2xl p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl text-[#F2EFEB]">{editing ? "Ürünü Düzenle" : "Yeni Ürün"}</h2>
                <button data-testid="close-form-btn" onClick={closeForm} className="text-[#8C8681] hover:text-[#F2EFEB]"><X strokeWidth={1.5} /></button>
              </div>

              <form onSubmit={save} className="space-y-5">
                <div>
                  <label className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681] block mb-2">Ürün Adı</label>
                  <input
                    data-testid="product-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-[#0D0C0B] border border-white/15 rounded-lg px-4 py-3 text-[#F2EFEB] outline-none focus:border-[#C67D57] transition-colors"
                    placeholder="Örn: Ceviz Kaplama"
                  />
                </div>

                <div>
                  <label className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681] block mb-2">Kategori</label>
                  <select
                    data-testid="product-category-select"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })}
                    className="w-full bg-[#0D0C0B] border border-white/15 rounded-lg px-4 py-3 text-[#F2EFEB] outline-none focus:border-[#C67D57] transition-colors"
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {SUB_BY_CAT[form.category] && (
                  <div>
                    <label className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681] block mb-2">Alt Kategori</label>
                    <select
                      data-testid="product-subcategory-select"
                      value={form.subcategory}
                      onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                      className="w-full bg-[#0D0C0B] border border-white/15 rounded-lg px-4 py-3 text-[#F2EFEB] outline-none focus:border-[#C67D57] transition-colors"
                    >
                      <option value="">Genel</option>
                      {SUB_BY_CAT[form.category].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}

                <div>
                  <label className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681] block mb-2">Açıklama</label>
                  <textarea
                    data-testid="product-description-input"
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full bg-[#0D0C0B] border border-white/15 rounded-lg px-4 py-3 text-[#F2EFEB] outline-none focus:border-[#C67D57] transition-colors resize-none"
                    placeholder="Ürün açıklaması..."
                  />
                </div>

                <div>
                  <label className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681] block mb-2">Görsel</label>
                  {form.image && (
                    <div className="mb-3 relative w-full aspect-video rounded-lg overflow-hidden border border-white/10">
                      <img src={imageUrl(form.image)} alt="önizleme" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="product-file-input" />
                    <button
                      type="button"
                      data-testid="upload-image-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-2 border border-white/15 text-[#F2EFEB] hover:border-[#C67D57] px-4 py-3 rounded-lg text-sm transition-colors disabled:opacity-50"
                    >
                      {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} strokeWidth={1.5} />}
                      Dosya Yükle
                    </button>
                  </div>
                  <div className="mt-2">
                    <input
                      data-testid="product-image-url-input"
                      value={form.image}
                      onChange={(e) => setForm({ ...form, image: e.target.value })}
                      className="w-full bg-[#0D0C0B] border border-white/15 rounded-lg px-4 py-2.5 text-[#F2EFEB] text-sm outline-none focus:border-[#C67D57] transition-colors"
                      placeholder="veya görsel bağlantısı (URL) yapıştırın"
                    />
                  </div>
                </div>

                <button
                  data-testid="save-product-btn"
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#F2EFEB] text-[#0D0C0B] hover:bg-[#C67D57] hover:text-[#F2EFEB] py-4 rounded-full font-sans uppercase tracking-[0.2em] text-xs transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  {editing ? "Güncelle" : "Ekle"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
